import { useParams, useNavigate } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useAuthContext } from "@/contexts/AuthContext"
import { useTournamentOrganizers, useTournamentInfo } from "@/graphql/hooks/useTournamentDetail"
import { useTournamentRounds } from "@/graphql/hooks/useTournamentRounds"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  AlertCircle,
  ExternalLink,
  Trophy,
  CalendarCheck,
  Users,
  AlertCircleIcon,
  Cog,
} from "lucide-react"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"

const roundStatus = {
  1: "En curso",
  2: "Terminada",
  3: "Anulada",
}

const TournamentRounds = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const navigate = useNavigate()
  const { userInfo, loading: authLoading, isAdmin, isAuthenticated } = useAuthContext()
  const { tournament, loading: tournamentLoading, error } = useTournamentInfo(Number(tournamentId))

  const { organizers, loading: loadingOrganizers } = useTournamentOrganizers(
    Number(tournamentId),
    isAuthenticated,
  )
  const { rounds, loading: roundsLoading } = useTournamentRounds(Number(tournamentId))

  // Check authorization
  const canAccess = () => {
    if (authLoading || !userInfo || !tournament) return false

    // Check if user is admin (we'll assume admins have categoryId 1 for now)
    if (isAdmin) {
      return true
    }

    // Check if user owns the store that owns this tournament
    const isStoreOwner = userInfo.User?.Person?.Store?.id === tournament.storeId

    const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)

    return isAdmin || isStoreOwner || isOrganizer
  }

  // Check if user can view private tournaments
  const canViewTournament = () => {
    if (!tournament) return false

    // If tournament is not private, everyone can view
    if (!tournament.isPrivate) return true

    // If tournament is private, only authorized users can view
    return canAccess()
  }

  if (authLoading || tournamentLoading || loadingOrganizers) {
    return (
      <Layout>
        <LoadingScreen message="Cargando información de las rondas..." />
      </Layout>
    )
  }

  if (error || !tournament) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-muted-foreground mb-4">
                Torneo no encontrado
              </h2>
              <p className="text-muted-foreground">
                No se pudo cargar la información del torneo solicitado.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  if (!canViewTournament()) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Este torneo es privado y no tienes permisos para verlo.
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    )
  }

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("es-ES", {
        weekday: "long",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "numeric",
        hour12: false,
      })
    } catch {
      return dateString
    }
  }

  return (
    <Layout>
      <HeaderTournament tournament={tournament} />
      <div className="container mx-auto px-0 py-0">
        {canAccess() ? (
          <TournamentNavigation
            tournament={tournament}
            tournamentName={tournament?.name || ""}
            tournamentStatusId={tournament.statusId}
            currentPage="rounds"
            showRounds
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5" />
                Rondas del Torneo ({rounds.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {roundsLoading ? (
                <LoadingScreen message="Cargando rondas..." />
              ) : rounds.length > 0 ? (
                <>
                  {/* --- Mobile: Cards --- */}
                  <div className="space-y-3 md:hidden">
                    {rounds.map((round, key) => (
                      <Card key={key} className="p-3 border">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium">{round.name}</span>
                          <Badge variant={round.statusId === 1 ? "secondary" : "default"}>
                            {roundStatus[round.statusId] || "N/A"}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                          <div>
                            <Users className="h-3 w-3 inline" /> Mesas: {round.totalMatches || 0}
                          </div>
                          {round.statusId === 1 && (
                            <div>
                              <AlertCircleIcon className="h-3 w-3 inline" /> Pendientes:{" "}
                              {round.totalMatchPendings || 0}
                            </div>
                          )}
                        </div>
                        <div className="text-muted-foreground text-sm">
                          <CalendarCheck className="h-3 w-3 inline" /> {formatDate(round.created)}
                        </div>
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {round.urlView && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`${round.urlView}/${tournamentId}`)}
                            >
                              <ExternalLink className="h-4 w-4 mr-1" />
                              Ver Ronda
                            </Button>
                          )}
                          {round.urlStanding && round.statusId === 2 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`${round.urlStanding}/${tournamentId}`)}
                            >
                              <Trophy className="h-4 w-4 mr-1" />
                              Posiciones
                            </Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* --- Desktop: Tabla --- */}
                  <div className="hidden md:block">
                    <Table className="border rounded-lg overflow-hidden">
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead rowSpan={2} className="font-semibold">
                            Ronda
                          </TableHead>
                          <TableHead rowSpan={2} className="font-semibold">
                            Estado
                          </TableHead>
                          <TableHead colSpan={3} className="font-semibold text-center">
                            Mesas
                          </TableHead>
                          <TableHead rowSpan={2} className="font-semibold text-center">
                            <CalendarCheck className="inline h-4 w-4 mr-1" />
                            Fecha de inicio
                          </TableHead>
                          <TableHead rowSpan={2} className="font-semibold text-center">
                            <Cog className="inline h-4 w-4 mr-1" />
                            Acciones
                          </TableHead>
                        </TableRow>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-center">Pendientes</TableHead>
                          <TableHead className="text-center">Reportadas</TableHead>
                          <TableHead className="text-center">Total</TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {rounds.map((round, key) => (
                          <TableRow
                            key={key}
                            className={
                              key % 2 === 0
                                ? "bg-white dark:bg-muted/10"
                                : "bg-muted/20 dark:bg-muted/5"
                            }
                          >
                            {/* Nombre de la ronda */}
                            <TableCell className="font-medium text-foreground">
                              {round.name}
                            </TableCell>

                            {/* Estado */}
                            <TableCell>
                              <Badge variant={round.statusId === 1 ? "secondary" : "default"}>
                                {roundStatus[round.statusId] || "N/A"}
                              </Badge>
                            </TableCell>

                            {/* Mesas */}
                            <TableCell className="text-center text-foreground">
                              {round.totalMatchPendings || 0}
                            </TableCell>
                            <TableCell className="text-center text-foreground">
                              {round.totalMatches - round.totalMatchPendings || 0}
                            </TableCell>
                            <TableCell className="text-center text-foreground">
                              {round.totalMatches || 0}
                            </TableCell>

                            {/* Fecha */}
                            <TableCell className="text-center text-sm text-muted-foreground">
                              {formatDate(round.created)}
                            </TableCell>

                            {/* Acciones */}
                            <TableCell>
                              <div className="flex flex-wrap gap-2 justify-center">
                                {round.urlView && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`${round.urlView}/${tournamentId}`)}
                                  >
                                    <ExternalLink className="h-4 w-4 mr-1" />
                                    Ver
                                  </Button>
                                )}
                                {round.urlStanding && round.statusId === 2 && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`${round.urlStanding}/${tournamentId}`)}
                                  >
                                    <Trophy className="h-4 w-4 mr-1" />
                                    Posiciones
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <p className="text-center text-muted-foreground py-8">No hay rondas creadas aún.</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  )
}

export default TournamentRounds
